/* Tilbud til LOS Marine – norsk (bokmål). Et digitalt tilbudsbrev med et produkt inni:
   brev → ett tilbud på fem minutter → prototypen → systemet i seks visninger → seks steg → KI → økonomisystemet → leveranse → pris → oss → veien videre. */
import { CASES } from "./cases";
import { nok, priceModel } from "./priceModel";

/* The price – numbers only. Every amount shown on the page and in the PDF is derived from these (see priceModel.js). */
export const LOS_PRICE = priceModel({
  modules: [
    { key: "salg", list: 175000, offer: 149000, monthly: 11900 },
    { key: "plan", list: 125000, offer: 100000, monthly: 4900 },
  ],
  packageMonthly: 14900,
  split: [50, 40, 10],
  months: 12,
});
const P = LOS_PRICE;
const PM = Object.fromEntries(P.modules.map((m) => [m.key, m]));

const T = "/tilbod/kviteberg/team/";
const L = "/tilbod/los/";
const byId = Object.fromEntries(CASES.map((c) => [c.id, c]));

export const UI_NB = {
  proposal: "Tilbud",
  draft: "Utkast",
  pdf: "Last ned PDF",
  pdfBusy: "Lager PDF …",
  skip: "Hopp til innholdet",
  to: "Til",
  from: "Fra",
  confidential: "Konfidensielt",
  validUntil: "Gyldig til",
  page: "Side",
  date: "Dato",
  reference: "Referanse",
  contact: "Kontakt",
  proposalTo: "Tilbud til",
  fileWord: "Tilbud",
  prototype: "Fungerende prototype med fiktive kundedata. Klikk på fanene.",
  yes: "Ja",
  no: "—",
  contents: "Innhold",
};

export const LOS_MARINE_NO = {
  kind: "product",
  pdf: "product",
  lang: "nb",
  ui: UI_NB,
  slug: "los-marine",
  ref: "KV-2026-LOS-01",
  status: "Tilbud",
  /* fixed issue date – the offer expires validDays later (it must not roll forward every time the page is opened) */
  issued: "2026-10-14",
  validDays: 30,
  brand: "LOS Marine",
  title: "Fra forespørsel til tilbud.\nSamlet i ett system.",
  to: { company: "LOS Marine AS", person: "Karl-Henrik Kongshavn", role: "Daglig leder", address: "Rubbestadneset", fill: false },
  from: { company: "Kviteberg AS", person: "Martin Kviteberg", role: "Ansvarlig for leveransen", email: "martin@kviteberg.no", address: "Kvinnherad, Norge", photo: "/tilbod/kviteberg/martin.jpg" },

  pages: ["cover", "letter", "showcase", "live", "idea", "flow", "ai", "visma", "delivery", "price", "terms", "about", "next"],

  hero: {
    sub: "Et tilbudssystem tilpasset LOS Marines arbeidsmåte. Forespørsler blir til arbeidsomfang, kalkyler og tilbud med utgangspunkt i deres egne prislister og satser. Dere beholder kontrollen over priser, forbehold og kapasitet.",
  },

  /* under brevet: fra forespørsel til ferdig tilbud – stegene til venstre, kundens tilbud til høyre */
  showcase: {
    eyebrow: "Demonstrasjon",
    title: "Se et tilbudsutkast\nbli til på fem minutter.",
    lede: "MS Nordlys, 5-årsklassing, fiktive data: en forespørsel på 114 sider blir til 28 arbeidsposter, kalkyle og tilbudsutkast på få minutter. LOS Marine avklarer usikkerhetene og godkjenner tilbudet før det sendes.",
    steps: [
      { t: "0:00", title: "Forespørselen kommer inn" },
      { t: "0:40", title: "Arbeidsposter og krav hentes ut" },
      { t: "1:30", title: "Omfang og forutsetninger avklares" },
      { t: "3:00", title: "Kalkylen fylles fra prisliste og satser" },
      { t: "4:30", title: "Tilbudsutkastet genereres" },
      { t: "5:00", title: "Klart til avklaring og kontroll" },
    ],
    url: "tilbud.los-marine.no/p/26112/tilbud/1",
    /* PDF only */
    img: L + "tilbud-c.jpg",
    imgCaption: "Tilbudet i prototypen etter kontroll: versjon 1, godkjent av daglig leder, klar til utsending. Fiktive kundedata.",
    tour: "Blar automatisk · rull for å bla selv",
    tourTouch: "Blar automatisk",
  },

  live: {
    eyebrow: "Prototype",
    title: "Prøv prototypen selv.",
    lede: "Den kjører med fiktive kundedata. Logg inn, åpne MS Nordlys og gå hele veien fra forespørsel til tilbud.",
    hint: "Fiktive kundedata",
    openFull: "Åpne prototypen",
    ctaSub: "Fullskjerm · innloggingsdetaljer er sendt separat",
    open: "Åpne i eget vindu",
    restart: "Start på nytt",
    close: "Lukk",
    showControls: "Vis kontroller",
    loading: "Laster prototypen …",
  },

  letter: {
    greeting: "Hei Karl-Henrik,",
    close: "Vennlig hilsen",
    p: [
      "Takk for muligheten til å presentere et tilbud til LOS Marine.",
      "Vi har satt oss inn i hvordan dere jobber fra forespørsel til tilbud i dag: spesifikasjoner på over hundre sider, kalkyle i Excel, tilbudsmal i Word og risikosjekkliste før utsending. Mye av tiden går til å lese, strukturere og flytte informasjon mellom dokumenter – før selve vurderingen kan begynne.",
      "Vi foreslår et system som tar dette forarbeidet, bygget på deres egne prislister, satser og rutiner. KI leser dokumentene, strukturerer arbeidspostene og skriver utkast. Priser, dekningsbidrag og godkjenninger styres fortsatt av LOS Marine – og dere skal alltid kunne se hvor grunnlaget kommer fra.",
      "For å gjøre forslaget konkret har vi allerede bygget en fungerende prototype – med utgangspunkt i en reell forespørsel dere har jobbet med, men vist her med fiktive data. Den er ment som et utgangspunkt for å utvikle den endelige løsningen sammen med dere.",
    ],
  },

  idea: {
    eyebrow: "Systemet",
    title: "Ett arbeidsområde.\nHele veien til kontrollert tilbud.",
    p: [
      "Saken bygges opp én gang og følger forespørselen hele veien – fra den kommer inn til tilbudet er kontrollert og sendt. Kundens krav, LOS Marines vurderinger og kalkylen holdes tydelig atskilt, slik at dere alltid ser hvor et tall eller en forutsetning kommer fra.",
      "Manglende prisgrunnlag og uavklarte forutsetninger markeres på saken og må løses før tilbudet kan godkjennes. En post som mangler kostnad eller pris, behandles aldri som null.",
    ],
    caption: "Saken i prototypen. Fiktive kundedata.",
    /* systemet i seks visninger – mockupen bytter mellom dem */
    views: [
      { key: "innboks", label: "Innboks", hint: "Nye forespørsler", caption: "Innboksen i prototypen: assistenten leser forespørselen, finner arbeidsposter og avklaringer, og oppretter saken. Fiktive kundedata." },
      { key: "arbeid", label: "Arbeid", hint: "Alle saker", caption: "Arbeidslisten i prototypen: hver sak med status, frist og neste steg – det som trenger deg, først. Fiktive kundedata." },
      { key: "sak", label: "Saken", hint: "MS Nordlys", caption: "Én sak i prototypen: arbeidsomfang, kalkyle og tilbud på samme flate – til versjonen er kontrollert og sendt. Fiktive kundedata." },
      { key: "ny", label: "Nytt arbeid", hint: "KI-assistent", caption: "Nytt arbeid i prototypen: forespørselen slippes inn, assistenten finner kunde, fartøy, arbeidsposter og avklaringer – og saken opprettes. Fiktive kundedata." },
      { key: "plan", label: "Plan & kapasitet", hint: "Dokk, slipp og kai", caption: "Plan & kapasitet i prototypen: kan uke 12–14 loves? Dokk, slipp, kai og fag mot bekreftet belegg – før perioden står i tilbudet. Fiktive kundedata." },
      { key: "hjem", label: "Hjem", hint: "Oversikt", caption: "Hjem i prototypen: det som trenger oppmerksomhet, der du slapp, nye forespørsler og åpne saker. Fiktive kundedata." },
    ],
    stages: ["Forespørsel", "Arbeidsomfang", "Kalkyle", "Tilbud", "Kontroll", "Sendt"],
    /* PDF only: two of the six views, side by side */
    pdfShots: [
      [L + "innboks-c.jpg", "Innboks: forespørselen leses av assistenten og blir en sak."],
      [L + "plan-c.jpg", "Plan & kapasitet: dokk, slipp, kai og fag mot bekreftet belegg."],
    ],
  },

  proto: {
    title: "Slik kan det se ut.",
    p: "Vi har allerede bygget en fungerende prototype for å gjøre diskusjonen konkret. Dataene er fiktive, men arbeidsflyten viser retningen.",
    caption: "Saken i prototypen, med fiktive kundedata.",
    img: L + "kalkyle.jpg",
    coverImg: L + "kalkyle.jpg",
  },

  flow: {
    title: null,
    head: "Seks steg",
    steps: [
      ["Forespørsel", "Forespørsler kommer inn ved videresending til én avtalt innboks i Microsoft 365, eller lastes opp manuelt – med spesifikasjon og vedlegg. Hver forespørsel blir en sak."],
      ["Arbeidsomfang", "Arbeidsposter med SFI- og WO-nummer struktureres. Kundens tekst beholdes urørt ved siden av LOS Marines arbeidsomfang, med kildevisning tilbake til dokumentet."],
      ["Kalkyle", "Timer, gjeldende prisliste og satser per avdeling gir kost, pris og dekningsbidrag – etter LOS Marines egne regler. Poster uten prisgrunnlag markeres; de blir aldri null."],
      ["Kapasitet", "Underveis i kalkylen sjekkes dokk, slipp, kai og fag mot registrert belegg og reservasjoner – før perioden loves i tilbudet."],
      ["Tilbud", "Kalkylen blir et digitalt, versjonsstyrt tilbudsutkast med følgebrev, forbehold, PDF og eksport til avtalte svararkmaler."],
      ["Kontroll og utsending", "Risikosjekkliste og godkjenning etter LOS Marines fullmaktsmatrise, dokumentert på saken. Først deretter sendes tilbudet."],
    ],
  },

  ai: {
    title: "KI som gjør forarbeidet.",
    p: ["KI skal gjøre tilbudsarbeidet raskere – ikke systemet mer komplisert.", "Den kan blant annet:"],
    list: ["lese forespørsler, spesifikasjoner og vedlegg", "finne arbeidsposter, SFI- og WO-nummer", "foreslå mengder og grunnlag fra kundens tekst", "fylle kalkylen fra prisliste og satser", "skrive førsteutkast til følgebrev og forbehold", "finne frem tidligere tilbud og priser – fra historikken som bygges i systemet og et avtalt utvalg eldre tilbud"],
    after: "Menneskene tar beslutningene. Systemet gjør forarbeidet og holder oversikten.",
    example: {
      from: "Forespørsel på 114 sider",
      to: "KI finner 28 arbeidsposter og 5 avklaringer",
      img: L + "ny.jpg",
      pdfImg: L + "ny-c.jpg",
      pdfCaption: "Nytt arbeid i prototypen: arbeidspostene utledet fra én forespørsel. Fiktive data.",
      caption: "Nytt arbeid i prototypen: fra tomt felt og én forespørsel til kunde, fartøy, arbeidsposter, avklaringer – og en opprettet sak. Fiktive data.",
    },
  },

  visma: {
    title: "Business Central blir værende\nder det er best.",
    p: [
      "LOS Marine bruker Microsoft Dynamics 365 Business Central til økonomi og ordre. Det fortsetter dere med – vi foreslår ikke å erstatte det.",
      "Det nye systemet blir arbeidsflaten for tilbudsprosessen, fra forespørsel til kontrollert tilbud. Når et tilbud er akseptert, opprettes kunde (om ny), prosjekt og ordrelinjer i Business Central fra det godkjente tilbudsgrunnlaget – for ordre, fakturering og regnskap. Kunderegisteret vedlikeholdes i Business Central.",
    ],
    line: ["LOS Marine Workspace", "Business Central"],
    under: ["Forespørsler · arbeidsomfang · kalkyle · tilbud · kontroll · kapasitet", "Kunder · prosjekter · ordre · fakturering · regnskap"],
    after: "Godkjent tilbudsgrunnlag brukes videre i Business Central, slik at dere unngår å registrere de samme opplysningene på nytt innenfor den avtalte dataflyten.",
    note: "Hovedomfanget – kunder, prosjekter og ordrelinjer – bekreftes i en teknisk avklaring mot LOS Marines oppsett i Business Central før avtaleinngåelse. Uke 1 brukes til detaljering, ikke til å avgjøre omfanget. Eventuelt arbeid hos LOS Marines Business Central-partner dekkes av LOS Marine.",
  },

  delivery: {
    title: "Planlagt produksjonsstart\netter seks uker.",
    p: "Prototypen gjør at vi kan starte med det viktigste: tilpasse løsningen til LOS Marine, legge inn gjeldende prislister, satser og fullmakter, teste på reelle forespørsler – og sette systemet i drift. Første leveranse er definert i dette tilbudet; uke 1 brukes til å låse detaljene.",
    weeks: 6,
    weekWord: "Uke",
    tail: { k: "Etter", name: "30 dagers oppfølging", short: "+30 d" },
    youLabel: "Dere bidrar med",
    resultLabel: "Resultat",
    phases: [
      {
        from: 1,
        to: 1,
        week: "Uke 1",
        name: "Forstå og låse",
        p: "Vi går gjennom dagens tilbudsflyt sammen med dere og låser detaljene i grunnlaget: prisliste 2026, timesatser per avdeling, fullmaktsmatrise, risikosjekkliste, tilbudsmal og svararkmaler. Dataflyten mot Business Central detaljeres innenfor omfanget som er bekreftet i avtalen.",
        you: "To arbeidsmøter, og tilgang til prisliste, maler og Business Central.",
        result: "Låste detaljer og et avklart datakart – det vi bygger etter.",
      },
      {
        from: 2,
        to: 3,
        week: "Uke 2–3",
        name: "Bygge og integrere",
        p: "Prototypen blir LOS Marines system. Prisliste, satser og DB-regler legges inn, tekstbiblioteket fylles med deres ordlyd, og Microsoft 365-innlogging og Business Central-integrasjonen settes opp og testes mot deres oppsett.",
        you: "Svar på avklaringer underveis, og én IT-kontakt for Microsoft 365 og Business Central.",
        result: "Et system med deres priser, tekster og innlogging – klart for ekte saker.",
      },
      {
        from: 4,
        to: 5,
        week: "Uke 4–5",
        name: "Teste på ekte saker",
        p: "Tilbudsansvarlig og den som kontrollerer bruker systemet på avtalte pilotsaker, parallelt med dagens arbeidsmåte. Kalkylene kontrolleres mot godkjent referansegrunnlag, tilbudsformatene prøves, og integrasjon og kapasitetsvisning testes. Vi sammenligner tidsbruk og arbeidsflyt med dagens metode, slik at dere får et konkret grunnlag for å vurdere gevinsten.",
        you: "To pilotbrukere og et avtalt utvalg pilotsaker.",
        result: "Godkjent pilot: pilotsakene er gjennomført og godkjent av LOS Marine.",
      },
      {
        from: 6,
        to: 6,
        week: "Uke 6",
        name: "Gå i produksjon",
        p: "Siste justeringer, opplæring av alle som skal bruke systemet, og produksjonssetting. Fra denne uken lages nye tilbud i det nye systemet.",
        you: "En halv dag til opplæring.",
        result: "LOS Marine Workspace er i kommersiell bruk.",
      },
    ],
    after: "Deretter følger 30 dager med tett oppfølging: vi følger de første sakene i drift, er tilgjengelige daglig og retter det som dukker opp.",
    note: "Fremdriften regnes fra avtalt prosjektoppstart og forutsetter at nødvendige tilganger, grunnlagsdata og avklaringer foreligger som planlagt. Eventuelle konsekvenser for fremdriften avklares underveis med LOS Marine.",
    includedTitle: "Slik er første leveranse avgrenset",
    includedItems: [
      ["Inntak av forespørsler", "Videresending til én avtalt innboks i Microsoft 365, og manuell opplasting. Webskjema og anbudsportaler leses inn som dokumenter."],
      ["Prislister og satser", "Gjeldende prislister og timesatser per avdeling, med versjonering. Prisliste 2026 er oppstartsgrunnlaget; autoriserte brukere vedlikeholder selv."],
      ["Svarark", "Eksport til inntil tre avtalte svararkmaler som identifiseres i uke 1. Nye kundeformater avtales separat."],
      ["Historikk", "Tilbud og priser fra historikken som bygges i systemet, pluss et avtalt utvalg eldre tilbud som importeres ved oppstart."],
      ["Business Central", "Kunde, prosjekt og ordrelinjer fra godkjent tilbud, innenfor omfanget som bekreftes før avtale. Kunderegisteret vedlikeholdes i Business Central."],
      ["Belegg og kapasitet", "Bekreftede oppdrag, reservasjoner og kapasitet per fag registreres og vedlikeholdes i modulen av LOS Marine. Aksepterte tilbud legges inn automatisk."],
      ["Forslag i Plan", "Oppstart, overtid og flyttede fagvinduer foreslås ut fra registrert kapasitet og avtalte regler – ikke automatisk optimalisering av hele verkstedet."],
      ["Manglende grunnlag", "Poster uten kostnad eller pris markeres og blokkerer godkjenning. De behandles aldri som null eller vises som sikre tall."],
    ],
    outside: "Prosjektoppfølging etter tilsagn, timeføring, innkjøp, kundeportal og import av belegg fra Business Central kan senere bygges på som egne moduler.",
  },

  price: {
    title: "To moduler.\nÉn plattform.",
    intro: [
      "Løsningen består av to moduler som prises separat og leveres samlet i første fase. Salgsmodulen er den mest omfattende leveransen – forespørselsbehandling med KI-støtte, kalkyle, kontroll og tilbud. Plan & kapasitet bygger videre på dataene fra salgsprosessen.",
      "Etableringen dekker tilpasning, integrasjon og innføring. Den løpende lisensen gir bruksrett til løsningen og inkluderer avtalt drift, vedlikehold og support. Prisene under er tilbudspriser til LOS Marine, og flere moduler kan legges til senere uten å endre det som er i drift.",
    ],
    /* the numbers behind every amount below (web reads these directly) */
    model: P,
    vat: "Alle priser eks. mva.",
    oneOffLabel: "Etablering",
    offerNote: "Tilbudspris · engangs",
    listLabel: "Listepris",
    monthlyLabel: "Programvarelisens",
    perMonth: "/ mnd",
    includesLabel: "Dette er med",
    /* web: the package first – what LOS Marine pays */
    pkg: {
      tag: "Begge moduler · pakkepris",
      oneOff: { label: "Etablering", sub: "Engangs · faktureres 50 / 40 / 10" },
      licence: { label: "Programvarelisens", sub: "Alle brukere · fra produksjonssetting" },
      introDiscount: "introduksjonsrabatt",
      packageDiscount: "pakkerabatt",
      separately: "hver for seg",
    },
    year: {
      title: "Første år",
      totalLabel: "Etablering + 12 måneders lisens",
      steps: ["Ved avtale", "Godkjent pilot", "I produksjon"],
      licence: "Lisens, 12 mnd",
      after: `Deretter ${nok(P.monthly)} / mnd. Lisensen kan sies opp med tre måneders varsel.`,
    },
    standalone: "Som enkeltmodul",
    standaloneLicence: "Lisens som enkeltmodul",
    receipt: {
      title: "Slik er prisen satt sammen",
      note: `Etableringen er tilbudspris per modul, med ${nok(P.introDiscount)} i introduksjonsrabatt. Lisensen blir ${nok(P.packageDiscount)} lavere per måned når begge modulene tas samlet.`,
      cols: ["Modul", "Etablering", "Lisens / mnd"],
      separate: "Sum, hver for seg",
      packageDiscount: "Pakkerabatt, begge moduler",
      pay: "Dere betaler",
    },
    modules: [
      {
        key: "salg",
        n: "01",
        tag: "Kjernemodul",
        name: "Salgsmodul",
        sub: "Fra forespørsel til kontrollert og sendt tilbud.",
        list: nok(PM.salg.list),
        offer: nok(PM.salg.offer),
        monthly: nok(PM.salg.monthly),
        features: [
          "Innboks med avtalt Microsoft 365-postboks og opplasting, saker, kunder og dokumenter",
          "KI leser forespørselen: arbeidsposter, SFI/WO, mengder og grunnlag – med kildevisning",
          "Gjeldende prislister og timesatser per avdeling, med versjonering",
          "Kalkyle med kost, pris og dekningsbidrag – manglende grunnlag markeres",
          "Risikosjekkliste og godkjenning etter LOS Marines fullmaktsmatrise",
          "Digitale tilbud, PDF, avtalte svararkmaler og versjonshistorikk",
          "Tekstbibliotek, Microsoft 365-innlogging og avtalt Business Central-integrasjon",
        ],
      },
      {
        key: "plan",
        n: "02",
        tag: "Tilleggsmodul",
        name: "Plan & kapasitet",
        sub: "Kan vi love uken – og hva kreves for å holde den?",
        list: nok(PM.plan.list),
        offer: nok(PM.plan.offer),
        monthly: nok(PM.plan.monthly),
        features: [
          "Belegg for flytedokk, slipp og kai",
          "Kapasitet per fag og avdeling, i timer per uke",
          "Maskiner og delt utstyr som egne ressurser",
          "Bekreftet, reservert og pipeline holdes atskilt",
          "Forslag til oppstart, overtid eller flyttet fagvindu – ut fra registrert kapasitet og avtalte regler",
          "Timebehovet hentes fra kalkylen; belegg og kapasitet registreres i modulen",
        ],
      },
    ],
    /* PDF total bar (same numbers) */
    total: {
      tag: "Begge moduler",
      offerLabel: "Etablering, tilbudspris",
      list: nok(P.list),
      offer: nok(P.offer),
      discount: `Introduksjonsrabatt ${nok(P.introDiscount)}`,
      monthlyLabel: "Programvarelisens, pakkepris",
      monthly: nok(P.monthly),
      monthlyList: nok(P.monthlySeparate),
      monthlyDiscount: `Pakkerabatt ${nok(P.packageDiscount)} / mnd`,
      monthlyNote: "Begge moduler, alle brukere. Starter ved produksjonssetting.",
      yearLabel: "Etablering + 12 måneders lisens",
      year: nok(P.firstYear),
      payLabel: "Betaling",
      pay: P.split.join(" / "),
      payNote: "Ved avtale · godkjent pilot · i produksjon",
    },
    /* PDF only */
    big: [
      [nok(P.offer), "Etablering, begge moduler"],
      [`${nok(P.monthly)} / mnd`, "Programvarelisens, pakkepris"],
      ["Ca. 6 uker", "Fra oppstart til produksjon"],
    ],
  },

  terms: {
    title: "Vilkår og betingelser",
    p: "Kort og uten overraskelser. Dette er rammene for etableringen og lisensen. Detaljerte vilkår for drift, support, sikkerhetskopiering og inkludert KI-bruk fastsettes i et drifts- og servicevedlegg som inngår i den endelige avtalen.",
    paymentTitle: "Betalingsplan for etableringen",
    payment: [
      { pct: P.split[0], k: "Ved avtaleinngåelse", v: "Avtale og leveranseomfang er bekreftet. Prosjektet starter.", amount: nok(P.payments[0]) },
      { pct: P.split[1], k: "Ved godkjent pilot", v: "Avtalte pilotsaker er gjennomført: kalkyler stemmer mot referansegrunnlag, tilbudsformatene fungerer, integrasjon og kapasitetsvisning er testet.", amount: nok(P.payments[1]) },
      { pct: P.split[2], k: "Ved produksjon", v: "LOS Marine tar systemet i kommersiell bruk.", amount: nok(P.payments[2]) },
    ],
    paymentNote: `Beløpene gjelder begge moduler til tilbudspris ${nok(P.offer)} eks. mva. Programvarelisensen (${nok(P.monthly)} / mnd) faktureres månedlig fra produksjonssetting.`,
    items: [
      ["Leveransetrygghet", "Avvik fra avtalt funksjonalitet rettes uten tillegg i pris. Nye funksjoner og endringer utenfor avtalt omfang spesifiseres og godkjennes skriftlig før de gjennomføres."],
      ["Programvarelisens", "Lisensen gjelder hele LOS Marine, alle brukere inkludert, og gir bruksrett til løsningen med drift, feilretting, oppdateringer og support. Den starter ved produksjonssetting, faktureres månedlig og kan sies opp med tre måneders varsel – uten bindingstid utover dette."],
      ["Support og KI-bruk", "Inkludert KI-bruk, håndtering av eventuelt overforbruk, supportkanal og svartider spesifiseres i drifts- og servicevedlegget. LOS Marine varsles før bruk kan medføre ekstra kostnad."],
      ["Drift og sikkerhet", "Driftsoppsett, datalagring og behandling gjennom KI-tjenester dokumenteres før avtaleinngåelse. Eventuelle særskilte krav til datalokalitet avklares teknisk og kommersielt før endelig avtale. Tilgangsstyring, logging og Microsoft 365-innlogging er inkludert."],
      ["Personopplysninger", "Databehandleravtale inngås, og behandlingsoppsettet godkjennes av LOS Marine, før piloten starter med reelle forespørsler – ikke først ved produksjonssetting."],
      ["Eierskap til data", "LOS Marine eier egne data. Ved opphør eksporteres saker, kalkyler, tilbud og dokumenter i åpne formater (Excel/CSV, PDF og originalfiler) innen 30 dager. Standard eksport inngår i lisensen."],
      ["Kontinuitet", "Kviteberg eier plattform og kildekode; LOS Marine har bruksrett så lenge lisensen løper. Dersom Kviteberg ikke lenger kan levere, er full dataeksport inkludert. Eventuell migrering og videre overgangsbistand avtales særskilt. Deponering av kildekode kan avtales."],
      ["Business Central", "Integrasjonens hovedomfang bekreftes før avtale. Lisenser og tilgang til Business Central, og eventuelt arbeid hos LOS Marines Business Central-partner, er LOS Marines ansvar."],
      ["Gyldighet", "Tilbudet gjelder til {validUntil}. Alle priser er oppgitt eks. mva."],
    ],
  },

  about: {
    title: "Hvem dere får hjelp av.",
    p: [
      "Kviteberg AS er et norsk konsulent- og programvareselskap i Kvinnherad. Vi kombinerer rådgivning med egen utvikling, slik at analysen og systemet som bygges, kommer fra samme hånd.",
      "Martin Kviteberg er ansvarlig for leveransen til LOS Marine – fra oppstart til produksjon og oppfølging – og er den dere kontakter i det daglige. Bertine, Per Ove og Sigrid Kviteberg bidrar i rådgivning og kvalitetssikring, og vi har et nettverk av spesialister der oppdraget krever det.",
      "Drift følges opp løpende: overvåking, feilretting og oppdateringer er en del av lisensen, og support går direkte til dem som har bygget løsningen. Vi kommer fra familien bak Adonis, forretningskritisk programvare brukt av rederier verden over siden 1988.",
    ],
    checks: ["Én ansvarlig for leveransen – fra uke 1 til drift", "Support fra dem som har bygget løsningen", "Dere eier egne data – full åpenhet"],
    team: [
      { name: "Martin Kviteberg", role: "Grunnlegger · Rådgivning og utvikling", img: T + "martin.jpg" },
      { name: "Bertine Kviteberg", role: "Rådgiver", img: T + "bertine.jpg" },
      { name: "Per Ove Kviteberg", role: "Strategisk partner", img: T + "per-ove.jpg" },
      { name: "Sigrid Kviteberg", role: "Rådgiver", img: T + "sigrid.jpg" },
    ],
    site: { label: "kviteberg.no", url: "https://kviteberg.no" },
    refsLabel: "Utvalgte leveranser",
    refs: [
      { id: "travelease", k: "TravelEase.AI" },
      { id: "vestmaskin", k: "Vest Maskinservice" },
      { id: "digihome", k: "DigiHome" },
    ],
    get refItems() {
      return this.refs.map((r) => ({ ...byId[r.id], ...r }));
    },
  },

  next: {
    title: "Veien videre",
    /* kept deliberately short: offer is complete, no more sales process required */
    p: ["Ta gjerne kontakt dersom dere har spørsmål eller ønsker å gå videre med tilbudet. Da avklarer vi de siste detaljene og avtaler oppstart."],
    cta: null,
    pdf: "Last ned tilbudet som PDF",
    subject: "Tilbud KV-2026-LOS-01 – LOS Marine",
    body: "Hei Martin,\n\nVi har noen spørsmål til tilbudet / ønsker å gå videre:\n\n",
  },
};
